import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMultiStepForm } from 'app/hooks/useMultiStepForm'
import IonIcon from '@reacticons/ionicons'
import { LoanParams } from 'app/models/loan-params'
import { FacilityParams } from 'app/models/facility-params'
import { LoanMergeParams } from 'app/models/loan-merge-params'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { getFacilities, getFacility } from 'app/state/ducks/facilities/selectors'
import { loadOneFacility } from 'app/state/ducks/facilities/thunks'
import { getIndexRateOptions } from 'app/state/ducks/index-rate-options/selectors'
import { loadIndexRateOptions } from 'app/state/ducks/index-rate-options/thunks'
import TermIndexRateForm from 'app/views/pages/deals-management/merges/term-index-rate-form'
import NonTermRateForm from 'app/views/pages/deals-management/merges/non-term-rate-form'
import StepperModal from 'app/views/components/modals/modal-stepper/StepperModal'
import { usePrerequisiteReads } from 'app/hooks/usePrerequisiteReads'
import { CustomLoader } from 'app/views/components/loader/loader'
import { ErrorNotification } from 'app/views/components/notifications/notification'


type MultiPartFormProps = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  selectedLoans?: LoanParams[]
  existingMergeData?: LoanMergeParams
}

enum Tab {
  TermIndexRate,
  NonTermRate,
}

export default function MergeLoansModal({ open, setOpen, selectedLoans, existingMergeData }: MultiPartFormProps) {
  const arrowIcon = <IonIcon name="arrow-forward-outline" style={{ color: '#1A56DB' }} />;

  const [tab, setTab] = useState<Tab>(Tab.TermIndexRate);
  const dispatch = useDispatch()
  const facilities = useSelector(getFacilities)
  const loadedFacility = useSelector(getFacility)
  const indexRateOptions = useSelector(getIndexRateOptions)
  const entities = useSelector(getEntities)

  // The merge steps read the loans' facility, the index rate options and the
  // entities from Redux; the Servicing page opens this modal with none of
  // them. The steps open once every read the store lacks has succeeded and
  // the facility is known — an existing scheduled-PIK merge saved while its
  // facility is unknown would discard its rates. A failed read is reported
  // and the modal closes; it never opens on an empty list.
  const facilityId = selectedLoans?.[0]?.facilityId?.id
  // The facility THIS opening read, kept here: the store's single-facility
  // slot is shared, and a request from a form closed earlier can overwrite it
  // after this modal is ready, which would unmount the steps into a loader.
  const [ownFacility, setOwnFacility] = useState<FacilityParams | undefined>()
  const facility: FacilityParams | undefined =
    facilities?.find(f => f.id === facilityId) ??
    (ownFacility?.id === facilityId ? ownFacility : undefined) ??
    (loadedFacility?.id === facilityId ? loadedFacility : undefined)
  const ready = usePrerequisiteReads(
    () => [
      ...(facilityId && !facilities?.some(f => f.id === facilityId)
        ? [dispatch(loadOneFacility(facilityId, 'Approved'))]
        : []),
      ...(indexRateOptions?.length ? [] : [dispatch(loadIndexRateOptions())]),
      ...(entities?.length ? [] : [dispatch(loadEntities())]),
    ],
    () => {
      ErrorNotification({
        title: 'Could not open',
        message: "The loans' facility, the index rate options or the entities could not be loaded. Please try again.",
      })
      setOpen(false)
    },
    {
      deps: [open, facilityId],
      enabled: open,
      onLoaded: results => {
        // the facility read, when one was issued, is the first result
        const read = (results[0] as { payload?: FacilityParams } | undefined)?.payload
        if (read?.id === facilityId) setOwnFacility(read)
      },
    },
  )

  const tabsConfig = [
    {
      tab: Tab.TermIndexRate,
      label: 'Term Index Rate',
      component: (
        <TermIndexRateForm
          facility={facility}
          selectedLoans={selectedLoans}
          closeModal={() => setOpen(false)}
          existingMergeData={existingMergeData}
        />
      ),
    },
    {
      tab: Tab.NonTermRate,
      label: 'Non-Term Rate',
      component: (
        <NonTermRateForm
          selectedLoans={selectedLoans}
          closeModal={() => setOpen(false)}
          existingMergeData={existingMergeData}
        />
      ),
      disabled: true,
      title: "Non-Term Rate loan merging is not yet available",
    },
  ]

  const {
    step,
    currentStepIndex,
    goTo
  } = useMultiStepForm(tabsConfig.map(config => config.component))

  const steps = tabsConfig.map(config => ({
    label: config.label,
    tab: config.tab,
    rightIcon: currentStepIndex === config.tab ? arrowIcon : null,
    disabled: config.disabled,
    title: config.title,
  }))

  return (
    <StepperModal<Tab>
      open={open}
      setOpen={setOpen}
      title="Merge Loans"
      subtitle="Merging Multiple Loans"
      steps={steps}
      currentTab={tab}
      setTab={setTab}
      goTo={(tabValue: Tab) => {
        setTab(tabValue);
        goTo(tabValue);
      }}
    >
      <>
        {ready && facility ? step : <CustomLoader />}
      </>
    </StepperModal>
  )
}